import { Text } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import type { MRT_ColumnDef, MRT_SortingState } from "mantine-react-table-open";
import {
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getDefaultMRTOptions } from "../../../../../mantine/getDefaultMRTOptions";
import { getDefaultMRTRowEditModeOptions } from "../../../../../mantine/getDefaultMRTRowEditModeOptions";
import { getMRTTopToolbarCustomActionsRenderer } from "../../../../../mantine/getMRTTopToolbarCustomActionsRenderer";
import { MantineIconCell } from "../../../../../mantine/mrt/components/cell/MantineIconCell";
import { useToast } from "../../../../../mantine/notifications/useToast";
import { type MeterMeasurementType } from "../../../../../types/api.types";
import { THEME_VARS } from "../../../../../utils/constants";
import {
  CONSUMER_TYPE_CHOICES,
  type Consumer,
  type TenantTableData,
  type TenantTableErrors
} from "../../../SiteSetupAssistant.types";
import { formatConsumersToFrontendTableData } from "../../../utils/formatConsumersToFrontendTableData";
import { formatFrontendTableDataToConsumers } from "../../../utils/formatFrontendTableDataToConsumers";
import { getLabelFromConsumerType } from "../../../utils/getLabelFromConsumerType";
import { getAddressColumn } from "./Columns/getAddressColumn";
import { getBillingAddressIsSameAsAddressColumn } from "./Columns/getBillingAddressIsSameAsAddressColumn";
import { getIsParticipatingInMieterstromColumn } from "./Columns/getIsParticipatingInMieterstromColumn";

const DEFAULT_SORTED: MRT_SortingState = [
  {
    id: "name",
    desc: false
  }
];

function ErrorCell() {
  return (
    <MantineIconCell
      iconColor={THEME_VARS.error[6]}
      IconComponent={IconAlertTriangle}
      renderedCellValue={
        <Text c="error">Bitte tragen Sie einen Wert ein.</Text>
      }
    />
  );
}

interface TenantTableProps {
  tenantTableErrors: TenantTableErrors | null;
  consumers: Array<Consumer>;
  defaultMeasurementType: MeterMeasurementType | null;
  onBlockNavigation: (blocked: boolean) => void;
  onChange: (data: Array<Consumer>) => void;
}

function TenantTable({
  tenantTableErrors,
  consumers,
  defaultMeasurementType,
  onBlockNavigation,
  onChange
}: TenantTableProps) {
  const { showErrorToast } = useToast();
  const [tableData, setTableData] = useState<Array<TenantTableData>>([]);

  useEffect(() => {
    if (consumers) {
      setTableData(formatConsumersToFrontendTableData(consumers));
    }
  }, [consumers]);

  const hasCellError = useCallback(
    (rowIndex: number, accessorKey: keyof TenantTableData) => {
      return (
        tenantTableErrors &&
        tenantTableErrors[rowIndex] &&
        tenantTableErrors[rowIndex][accessorKey] &&
        !tableData[rowIndex][accessorKey]
      );
    },
    [tableData, tenantTableErrors]
  );

  const columns = useMemo<Array<MRT_ColumnDef<TenantTableData>>>(
    () => [
      {
        accessorKey: "name",
        header: "Bezeichnung des Verbrauchers",
        sortingFn: (a, b) => {
          const aName = a.original.name;
          const bName = b.original.name;
          if (!aName || !bName) return 0;
          return aName.localeCompare(bName, undefined, {
            numeric: true
          });
        },
        mantineEditTextInputProps: () => {
          return {
            "aria-label": "Bezeichnung des Verbrauchers",
            required: true
          };
        }
      },
      {
        accessorKey: "type",
        header: "Typ des Verbrauchers",
        editVariant: "select",
        mantineEditSelectProps: () => {
          return {
            "aria-label": "Typ des Verbrauchers",
            data: CONSUMER_TYPE_CHOICES,
            required: true
          };
        },
        Cell: ({ row }) => {
          const hasError = hasCellError(row.index, "type");
          return (
            <div>
              {getLabelFromConsumerType(row.original.type)}
              {hasError && <ErrorCell />}
            </div>
          );
        }
      },
      {
        accessorKey: "tenantName",
        header: "Name des Mieters",
        Cell: ({ row }) => {
          const hasError = hasCellError(row.index, "tenantName");
          return (
            <div>
              {row.original.tenantName}
              {hasError && <ErrorCell />}
            </div>
          );
        },
        mantineEditTextInputProps: () => {
          return {
            "aria-label": "Name des Mieters",
            required: true
          };
        }
      },
      getIsParticipatingInMieterstromColumn(hasCellError),
      getAddressColumn(hasCellError),
      getBillingAddressIsSameAsAddressColumn(hasCellError)
    ],
    [hasCellError]
  );

  function handleChangeDeleteTenant(tenantToDelete: TenantTableData) {
    const newData = [...tableData];
    const indexToDelete = newData.findIndex(
      (tenant) => tenant === tenantToDelete
    );
    if (indexToDelete !== -1) {
      newData.splice(indexToDelete, 1);
      onChange(formatFrontendTableDataToConsumers(newData));
    } else {
      showErrorToast("Fehler beim Löschen des Mieters");
    }
  }

  const table = useMantineReactTable({
    ...getDefaultMRTOptions<TenantTableData>({
      emptyRowsFallbackText: "Hier können Sie die Mieter eintragen!",
      initialState: {
        sorting: DEFAULT_SORTED
      },
      hideBorder: true
    }),
    ...getMRTTopToolbarCustomActionsRenderer<TenantTableData>({
      create: {
        text: "Verbraucher hinzufügen",
        onClick: () => {
          table.setCreatingRow(true);
          onBlockNavigation(true);
        }
      },
      delete: {
        hide: true
      }
    }),
    ...getDefaultMRTRowEditModeOptions<TenantTableData>({
      rowActions: {
        edit: {
          text: "Mieter bearbeiten",
          onClick: () => {
            onBlockNavigation(true);
          }
        },
        delete: {
          text: "Mieter löschen",
          onClick: (row) => handleChangeDeleteTenant(row.original)
        }
      }
    }),
    columns,
    data: tableData,
    enableRowSelection: false,
    enableToolbarInternalActions: false,
    enableFilters: false,
    onCreatingRowCancel: () => {
      onBlockNavigation(false);
    },
    onEditingRowCancel: () => {
      onBlockNavigation(false);
    },
    onCreatingRowSave: ({ values }) => {
      const formattedValues = Object.keys(values).reduce(
        (result, key) => ({
          ...result,
          [key]: values[key] === "" ? null : values[key]
        }),
        {
          meter: {
            name: `Zähler ${values.name}`,
            is_calibrated: true,
            measurement_type: defaultMeasurementType
          }
        }
      );
      onChange(
        formatFrontendTableDataToConsumers([
          ...tableData,
          formattedValues as TenantTableData
        ])
      );
      table.setCreatingRow(null);
      onBlockNavigation(false);
    },
    onEditingRowSave: ({ values, row }) => {
      const formattedValues = Object.keys(values).reduce(
        (result, key) => ({
          ...result,
          [key]: values[key] === "" ? null : values[key]
        }),
        {
          meter: {
            name: `Zähler ${values.name}`,
            is_calibrated: true,
            measurement_type: defaultMeasurementType
          }
        }
      );
      const newData = [...tableData];
      newData[row.index] = {
        ...tableData[row.index],
        ...formattedValues,
        meter: { ...tableData[row.index].meter, name: `Zähler ${values.name}` }
      };
      onChange(formatFrontendTableDataToConsumers(newData));
      table.setEditingRow(null);
      onBlockNavigation(false);
    }
  });

  return (
    <div className="TenantTable">
      <MantineReactTable table={table} />
    </div>
  );
}

export { TenantTable };
